import { environment } from 'src/environments/environment';
import { Awards } from 'src/app/model/award';
import { StoreCategory, StoreProduct, StoreSortKey } from 'src/app/model/store';

/**
 * Normaliza texto para busca e agrupamento: remove acento, baixa a caixa e
 * colapsa espaços. Usado para comparar nome e categoria, que vêm do cadastro
 * como texto livre e chegam com grafias divergentes.
 */
export function normalizeText(value: string): string {
  if (!value) {
    return '';
  }
  return value
    .toString()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');
}

/**
 * Monta a URL da imagem do prêmio a partir da chave salva no bucket.
 * Devolve '' quando não há imagem, para o template cair no placeholder.
 */
export function resolveAwardImage(image?: string): string {
  if (!image) {
    return '';
  }
  const key = image.trim();
  if (!key) {
    return '';
  }
  if (/^https?:\/\//i.test(key)) {
    return key;
  }
  const base = environment.bucketUrl || '';
  return base.replace(/\/+$/, '') + '/' + key.replace(/^\/+/, '');
}

/** Um prêmio só é escondido quando está explicitamente inativo: `status` é nullable na API. */
export function isAwardActive(status?: boolean): boolean {
  return status !== false;
}

/** Acrescenta os campos derivados usados por busca, filtro e ordenacao. */
export function toStoreProduct(award: Awards): StoreProduct {
  const categoryLabel = (award.category || '').trim();
  return {
    ...award,
    image: resolveAwardImage(award.image),
    categoryKey: normalizeText(categoryLabel),
    categoryLabel,
    searchKey: normalizeText(award.name) + ' ' + normalizeText(categoryLabel),
    sortName: normalizeText(award.name),
  };
}

/**
 * Monta os chips a partir dos valores distintos de `category`.
 *
 * A categoria e texto livre no cadastro, entao "brindes", "Brindes" e
 * "BRINDES" viram um chip so. O rotulo exibido e a grafia mais frequente -
 * empate resolvido alfabeticamente, para o rotulo nao oscilar entre cargas.
 *
 * Ordem alfabetica de proposito, nao por contagem: contagem muda quando o
 * estoque muda e os chips pulariam de lugar entre sessoes.
 */
export function buildCategories(products: StoreProduct[]): StoreCategory[] {
  const groups: { [key: string]: { count: number; labels: { [label: string]: number } } } = {};

  (products || []).forEach((product) => {
    const key = product.categoryKey;
    if (!key) {
      return;
    }
    if (!groups[key]) {
      groups[key] = { count: 0, labels: {} };
    }
    groups[key].count += 1;
    const label = product.categoryLabel;
    groups[key].labels[label] = (groups[key].labels[label] || 0) + 1;
  });

  return Object.keys(groups)
    .map((key) => {
      const labels = groups[key].labels;
      const label = Object.keys(labels).sort((a, b) => {
        const diff = labels[b] - labels[a];
        return diff !== 0 ? diff : a.localeCompare(b, 'pt-BR');
      })[0];
      return { key, label, count: groups[key].count };
    })
    .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
}

/** Ordena uma COPIA: ordenar no lugar mexeria na lista de origem. */
export function sortProducts(products: StoreProduct[], sort: StoreSortKey): StoreProduct[] {
  const list = (products || []).slice();
  switch (sort) {
    case 'POINTS_ASC':
      return list.sort((a, b) => a.value - b.value || a.sortName.localeCompare(b.sortName, 'pt-BR'));
    case 'POINTS_DESC':
      return list.sort((a, b) => b.value - a.value || a.sortName.localeCompare(b.sortName, 'pt-BR'));
    case 'NAME_ASC':
      return list.sort((a, b) => a.sortName.localeCompare(b.sortName, 'pt-BR'));
    case 'NAME_DESC':
      return list.sort((a, b) => b.sortName.localeCompare(a.sortName, 'pt-BR'));
    default:
      // RELEVANCE: mantem a ordem que a API devolveu.
      return list;
  }
}

/**
 * Alinha o rotulo de categoria dos produtos ao rotulo canonico do chip, para o
 * card nao exibir "casa & cozinha" ao lado de "Casa & Cozinha".
 */
export function applyCanonicalCategoryLabels(
  products: StoreProduct[],
  categories: StoreCategory[]
): void {
  const labelByKey: { [key: string]: string } = {};
  (categories || []).forEach((category) => (labelByKey[category.key] = category.label));
  (products || []).forEach((product) => {
    if (product.categoryKey && labelByKey[product.categoryKey]) {
      product.categoryLabel = labelByKey[product.categoryKey];
    }
  });
}
